import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  TooltipContainer,
  TooltipHeader,
  HeaderLeft,
  IconCircle,
  HeaderTitle,
  HeaderActions,
  IconButton,
  TooltipDescription,
  TooltipMeta,
  MetaItem,
  MetaIcon,
  getSeverityColor,
  formatAnnotationType,
  formatDuration
} from './TooltipCommon';
import { ClockIcon, UserIcon } from './MetaIcons';
import { getAnnotationColor, getAnnotationIcon, formatAnnotationUserWithRole } from '../utils/annotationPresentation';

const RepairAnnotationTooltip = ({ annotation, position, onMouseEnter, onMouseLeave, onEdit }) => {
  const { t } = useTranslation();
  const typeColor = getAnnotationColor(annotation, { respectSeverity: false });
  const typeIcon = getAnnotationIcon(annotation.type);
  // eslint-disable-next-line no-unused-vars
  const severityColor = getSeverityColor(annotation.severity);

  const baseMetadata = annotation.base_metadata || {};

  // expected fields: repair_name (or name) and description
  const repairName = baseMetadata.repair_name || baseMetadata.name || annotation.title;

  const tooltip = (
    <TooltipContainer
      style={{ left: position.x, top: position.y }}
      placement={position.placement || 'top'}
      align={position.align || 'center'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <TooltipHeader>
        <HeaderLeft>
          <IconCircle bg={typeColor}>
            <span aria-hidden="true">{typeIcon}</span>
          </IconCircle>
          <HeaderTitle>{formatAnnotationType(annotation.type)}</HeaderTitle>
        </HeaderLeft>
        <HeaderActions>
          {annotation && annotation.source_id ? (
            <IconButton
              onClick={() => window.open(`/repairs/${annotation.source_id}`, '_blank')}
              title={t('annotations.tooltip.viewDetails')}
            >
              ↗
            </IconButton>
          ) : (
            <IconButton onClick={() => (onEdit && onEdit(annotation))} title={t('annotations.tooltip.viewDetails')}>↗</IconButton>
          )}
        </HeaderActions>
      </TooltipHeader>

      {repairName && (
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'inherit' }}>
          {repairName}
        </div>
      )}

      {annotation.description && (
        <TooltipDescription>{annotation.description}</TooltipDescription>
      )}

      <TooltipMeta>
        <div className="meta-row">
          <MetaItem>
            <MetaIcon><ClockIcon size={14} /></MetaIcon>
            <span>{formatDuration(annotation.start_time, annotation.end_time)}</span>
          </MetaItem>
          <MetaItem>
            <MetaIcon><UserIcon size={14} /></MetaIcon>
            <span>{formatAnnotationUserWithRole(t, annotation)}</span>
          </MetaItem>
        </div>
      </TooltipMeta>

      {/* Bottom actions removed: header contains View Details */}
    </TooltipContainer>
  );

  return createPortal(tooltip, document.body);
};

RepairAnnotationTooltip.propTypes = {
  annotation: PropTypes.object.isRequired,
  position: PropTypes.object.isRequired,
  showControls: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onEdit: PropTypes.func
};

export default RepairAnnotationTooltip;
